import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row, Select, Spin, message } from 'antd';
import { withRouter } from 'react-router-dom';
import { Chart, Geom, Axis, Tooltip as ChartTooltip, Legend } from 'bizcharts';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { MONTH_FORMAT } from 'utils/constants';
import { getDashboardSummary, listServicePackages } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { ALL_PROPERTIES, readPageFilters, toMonthDate, useSyncPageFilters } from './pageFilters';

const normalizeId = id => (id && id._id ? String(id._id) : String(id));

const propertyIdOfUnit = unit => {
  const property = unit && unit.roomType && unit.roomType.property;
  if (!property) return '';
  if (typeof property === 'object') return String(property._id || '');
  return String(property);
};

const formatMoney = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPct = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00%';
  return `${num.toFixed(2)}%`;
};

const formatDelta = (value, { isPercent = false, isMoney = false } = {}) => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const num = Number(value);
  const sign = num > 0 ? '▲' : num < 0 ? '▼' : '•';
  const color = num > 0 ? '#52c41a' : num < 0 ? '#ff4d4f' : 'rgba(0,0,0,0.45)';
  let label;
  if (isPercent) label = `${Math.abs(num).toFixed(2)}%`;
  else if (isMoney) label = formatMoney(Math.abs(num));
  else label = String(Math.abs(num));
  return { sign, color, label };
};

const DeltaLine = ({ delta, isPercent, isMoney }) => {
  const info = formatDelta(delta, { isPercent, isMoney });
  if (!info) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: info.color, fontWeight: 600 }}>
      {info.sign} {info.label}
    </div>
  );
};

const DashboardPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [filterPropertyId, setFilterPropertyId] = useState(
    () => readPageFilters(location && location.search).propertyId || ALL_PROPERTIES
  );
  const [servicePackageId, setServicePackageId] = useState(() => readPageFilters(location && location.search).servicePackageId);
  const [monthDate, setMonthDate] = useState(() => readPageFilters(location && location.search).monthDate);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const year = monthDate.year();
  const month = monthDate.month() + 1;

  const packagesForProperty = useMemo(() => {
    const list = packages || [];
    if (!filterPropertyId || filterPropertyId === ALL_PROPERTIES) return list;
    const unitIds = new Set(
      (hostUnits || [])
        .filter(u => String(propertyIdOfUnit(u)) === String(filterPropertyId))
        .map(u => String(u._id))
    );
    if (!unitIds.size) return [];
    return list.filter(pkg => (pkg.units || []).some(uid => unitIds.has(normalizeId(uid))));
  }, [packages, hostUnits, filterPropertyId]);

  useEffect(() => {
    if (!hostId) {
      setPackages([]);
      setProperties([]);
      setHostUnits([]);
      setFilterPropertyId(ALL_PROPERTIES);
      setServicePackageId(undefined);
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setPackages([]);
    setProperties([]);
    setHostUnits([]);
    setPackagesLoading(true);
    setPropertiesLoading(true);
    Promise.all([listServicePackages(hostId), loadHostProperties(hostId), loadHostUnits(hostId)])
      .then(results => {
        if (cancelled) return;
        const list = Array.isArray(results[0]) ? results[0] : [];
        setPackages(list);
        setProperties(results[1] || []);
        setHostUnits(results[2] || []);
      })
      .catch(() => {
        if (!cancelled) {
          message.error('Failed to load dashboard filters');
          setPackages([]);
          setProperties([]);
          setHostUnits([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPackagesLoading(false);
          setPropertiesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hostId]);

  useEffect(() => {
    if (!hostId || packagesLoading) return;
    setServicePackageId(prev => {
      if (prev && packagesForProperty.some(p => String(p._id) === String(prev))) return prev;
      return packagesForProperty[0] ? String(packagesForProperty[0]._id) : undefined;
    });
  }, [hostId, packagesLoading, packagesForProperty]);

  useEffect(() => {
    if (!hostId || propertiesLoading) return;
    if (!filterPropertyId || filterPropertyId === ALL_PROPERTIES) return;
    if (properties.some(p => String(p._id) === String(filterPropertyId))) return;
    setFilterPropertyId(ALL_PROPERTIES);
  }, [hostId, propertiesLoading, properties, filterPropertyId]);

  useSyncPageFilters(history, location, {
    property: filterPropertyId,
    servicePackage: servicePackageId,
    date: monthDate.format(MONTH_FORMAT),
  });

  useEffect(() => {
    if (!hostId || !servicePackageId) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getDashboardSummary(hostId, { servicePackageId, year, month })
      .then(data => {
        if (!cancelled) setSummary(data);
      })
      .catch(err => {
        if (!cancelled) {
          message.error((err && err.message) || 'Failed to load dashboard');
          setSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hostId, servicePackageId, year, month]);

  const selected = summary && summary.selected;
  const months = (summary && summary.months) || [];

  const occupancyChartData = useMemo(
    () =>
      months.map(m => ({
        month: m.label,
        Occupancy: Number(m.occupancyRate) || 0,
      })),
    [months]
  );

  const revenueChartData = useMemo(
    () =>
      months.map(m => ({
        month: m.label,
        Revenue: Number(m.revenue) || 0,
      })),
    [months]
  );

  return (
    <Card title="Owner Reporting — Dashboard">
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item label="Filter Property" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder="All"
                value={filterPropertyId || ALL_PROPERTIES}
                onChange={val => setFilterPropertyId(val ? String(val) : ALL_PROPERTIES)}
                disabled={!hostId}
                loading={propertiesLoading}
              >
                <Select.Option value={ALL_PROPERTIES}>All</Select.Option>
                {properties.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item label="Service package" required style={{ marginBottom: 0 }}>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={packagesLoading ? 'Loading packages...' : 'Select service package'}
                value={servicePackageId}
                onChange={val => setServicePackageId(val ? String(val) : undefined)}
                disabled={!hostId || packagesLoading}
                loading={packagesLoading}
              >
                {packagesForProperty.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name || String(p._id)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Month" style={{ marginBottom: 0 }}>
              <MonthPicker defaultValue={monthDate} onChange={val => setMonthDate(toMonthDate(val))} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {!hostId && <p style={{ color: 'rgba(0,0,0,0.45)' }}>Select a host to view the dashboard.</p>}
      {hostId && !packagesLoading && packages.length === 0 && (
        <p style={{ color: 'rgba(0,0,0,0.45)' }}>No service packages found for this host.</p>
      )}
      {hostId && !packagesLoading && packages.length > 0 && packagesForProperty.length === 0 && (
        <p style={{ color: 'rgba(0,0,0,0.45)' }}>No service packages found for this property.</p>
      )}

      {hostId && servicePackageId && (
        <Spin spinning={loading}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Total Booked Nights</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {selected
                    ? `${selected.bookedNights || 0} / ${selected.availableNights || 0}`
                    : '0 / 0'}
                </div>
                <DeltaLine delta={selected && selected.occupancyDelta} isPercent />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Occupancy Rate</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {formatPct(selected && selected.occupancyRate)}
                </div>
                <DeltaLine delta={selected && selected.occupancyDelta} isPercent />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Total Revenue</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {formatMoney(selected && selected.revenue)}
                </div>
                <DeltaLine delta={selected && selected.revenueDelta} isMoney />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12} style={{ marginBottom: 16 }}>
              <Card size="small" title="Occupancy Trend">
                {occupancyChartData.length > 0 ? (
                  <Chart height={280} data={occupancyChartData} forceFit padding={[24, 40, 48, 48]}>
                    <Axis name="month" />
                    <Axis name="Occupancy" label={{ formatter: v => `${v}%` }} />
                    <ChartTooltip />
                    <Legend />
                    <Geom type="line" position="month*Occupancy" color="#13c2c2" size={2} />
                    <Geom type="point" position="month*Occupancy" color="#13c2c2" size={4} shape="circle" />
                  </Chart>
                ) : (
                  <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
                    No occupancy data
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12} style={{ marginBottom: 16 }}>
              <Card size="small" title="Revenue Trend">
                {revenueChartData.length > 0 ? (
                  <Chart height={280} data={revenueChartData} forceFit padding={[24, 40, 48, 56]}>
                    <Axis name="month" />
                    <Axis
                      name="Revenue"
                      label={{
                        formatter: v => {
                          const n = Number(v);
                          if (!Number.isFinite(n)) return v;
                          if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}K`;
                          return String(n);
                        },
                      }}
                    />
                    <ChartTooltip />
                    <Legend />
                    <Geom type="line" position="month*Revenue" color="#722ed1" size={2} />
                    <Geom type="point" position="month*Revenue" color="#722ed1" size={4} shape="circle" />
                  </Chart>
                ) : (
                  <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
                    No revenue data
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      )}
    </Card>
  );
};

export default withRouter(DashboardPage);
